import Head from 'next/head';
import Link from 'next/link';
import { useEffect, useState, type ReactNode } from 'react';
import Bugsnag, { type Breadcrumb } from '@bugsnag/js';
import { ArrowRight, ImageIcon, Lock, Terminal } from 'lucide-react';
import { linkClass } from '@/components/converter/PageParts';
import { gzipSize } from '@/utils/svg-tools/svg-source';

// Page parts for the free SVG tools (/svg-viewer, /svg-to-react, /svg-to-data-uri). They
// follow the converter pages' layout (see ConverterParts) without the examples gallery data.

const SITE_URL = 'https://svg.new';

export const btnPrimary =
  'inline-flex items-center justify-center gap-2 h-10 px-5 rounded-md text-[14px] font-medium bg-sky text-sky-ink hover:bg-sky-soft transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-wait';
export const btnSecondary =
  'inline-flex items-center justify-center gap-2 h-10 px-5 rounded-md text-[14px] font-medium text-ink-soft border border-cream-300 bg-cream-50 hover:border-cream-400 hover:bg-cream-100 transition-colors';
export const btnSmall =
  'inline-flex items-center justify-center gap-1.5 h-8 px-2.5 rounded-md text-[13px] font-medium text-ink-soft border border-cream-300 bg-white hover:bg-cream-100 hover:border-cream-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky';

export interface FaqItem {
  q: string;
  a: string;
}

// ---------------------------------------------------------------------------
// <head>: title, description, social tags and JSON-LD
// ---------------------------------------------------------------------------

export function ToolHead({
  path,
  title,
  description,
  socialTitle,
  appName,
  crumb,
  featureList,
  faq,
}: {
  path: string;
  title: string;
  description: string;
  socialTitle: string;
  appName: string;
  crumb: string;
  featureList: string[];
  faq: FaqItem[];
}) {
  const url = `${SITE_URL}${path}`;
  const appSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: appName,
    url,
    description,
    applicationCategory: 'DeveloperApplication',
    operatingSystem: 'Any',
    browserRequirements: 'Requires a modern web browser',
    isAccessibleForFree: true,
    featureList,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    publisher: { '@type': 'Organization', name: 'SVG.new', url: SITE_URL },
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'SVG.new', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: crumb, item: url },
    ],
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };
  // JSON-LD with "<" escaped, so text such as "<script>" in an answer can't end the tag early.
  const ld = (data: object) => ({ __html: JSON.stringify(data).replace(/</g, '\\u003c') });
  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link key="canonical" rel="canonical" href={url} />
      <meta key="og:title" property="og:title" content={socialTitle} />
      <meta key="og:description" property="og:description" content={description} />
      <meta key="og:url" property="og:url" content={url} />
      <meta key="twitter:title" name="twitter:title" content={socialTitle} />
      <meta key="twitter:description" name="twitter:description" content={description} />
      <script key="ld-app" type="application/ld+json" dangerouslySetInnerHTML={ld(appSchema)} />
      <script key="ld-breadcrumb" type="application/ld+json" dangerouslySetInnerHTML={ld(breadcrumbSchema)} />
      <script key="ld-faq" type="application/ld+json" dangerouslySetInnerHTML={ld(faqSchema)} />
    </Head>
  );
}

// ---------------------------------------------------------------------------
// Hero with the tool under it
// ---------------------------------------------------------------------------

export function ToolHero({
  crumb,
  before,
  highlight,
  after,
  intro,
  children,
}: {
  crumb: string;
  before: string;
  highlight: string;
  after: string;
  intro: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="relative bg-dot-pattern">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-8 pt-6 pb-12 sm:pt-8 sm:pb-16 flex flex-col items-center">
        <nav aria-label="Breadcrumb" className="w-full text-[13px] text-ink-faint">
          <Link href="/" className="hover:text-ink transition-colors">SVG.new</Link>
          <span className="mx-2" aria-hidden="true">/</span>
          <span aria-current="page" className="text-ink-muted">{crumb}</span>
        </nav>
        <h1 className="mt-8 sm:mt-10 text-center text-[34px] sm:text-[48px] md:text-[56px] font-bold tracking-[-0.035em] text-ink leading-[1.05]">
          {before}
          <span className="bg-gradient-to-r from-sky-bright via-sky to-sky-soft bg-clip-text text-transparent">{highlight}</span>
          {after}
        </h1>
        <p className="mt-4 text-[15px] sm:text-[16px] text-ink-muted text-center max-w-[640px] leading-relaxed">{intro}</p>
        <p className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-cream-200 bg-white px-3 py-1 text-[12.5px] font-medium text-ink-soft">
          <Lock className="h-3.5 w-3.5 text-sky" aria-hidden="true" />
          Runs in your browser, nothing is uploaded
        </p>
        <div className="mt-7 sm:mt-8 w-full min-w-0">{children}</div>
      </div>
    </section>
  );
}

/** The white card the tool sits in. */
export function ToolCard({ children }: { children: ReactNode }) {
  return <div className="w-full min-w-0 rounded-2xl border border-cream-200 bg-white shadow-sm">{children}</div>;
}

// ---------------------------------------------------------------------------
// Small controls
// ---------------------------------------------------------------------------

export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-md border border-cream-300 bg-cream-50 p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={`h-7 px-2.5 rounded text-[12.5px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky ${
            value === o.value ? 'bg-white text-ink shadow-sm' : 'text-ink-muted hover:text-ink'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Checkbox({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label className="inline-flex items-center gap-2 text-[13px] text-ink-soft cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-cream-400 accent-[rgb(var(--sky-rgb))]"
      />
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** False during the server render and the first client render, then true. */
export function useMounted(): boolean {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
}

/** Gzipped size of text, worked out in the browser after a short pause in typing. */
export function useGzipSize(text: string | null): number | null {
  const [result, setResult] = useState<{ text: string; size: number | null } | null>(null);
  useEffect(() => {
    if (text === null) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      gzipSize(text).then((size) => {
        if (!cancelled) setResult({ text, size });
      });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [text]);
  return result && result.text === text ? result.size : null;
}

/**
 * Bugsnag's click breadcrumbs record up to 140 characters of the clicked element's text,
 * which on these pages could be the visitor's SVG code. Blank that text while a tool page is
 * open, so "nothing is uploaded" stays true even if an error report goes out.
 */
export function useLocalOnlyBreadcrumbs() {
  useEffect(() => {
    if (!Bugsnag.isStarted()) return;
    const blank = (breadcrumb: Breadcrumb) => {
      if (breadcrumb.type === 'user' && breadcrumb.metadata) breadcrumb.metadata.targetText = '[svg tool]';
    };
    Bugsnag.addOnBreadcrumb(blank);
    return () => Bugsnag.removeOnBreadcrumb(blank);
  }, []);
}

// ---------------------------------------------------------------------------
// Sections under the tool
// ---------------------------------------------------------------------------

export function HowTo({ steps }: { steps: { title: string; text: ReactNode }[] }) {
  return (
    <ol className="max-w-[760px] space-y-6">
      {steps.map((step, i) => (
        <li key={step.title} className="flex items-start gap-4">
          <span className="w-7 h-7 rounded-full bg-sky text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
            {i + 1}
          </span>
          <div className="min-w-0">
            <h3 className="text-[16px] font-semibold text-ink">{step.title}</h3>
            <p className="mt-1.5 text-[15px] leading-relaxed text-ink-soft">{step.text}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

/** The two ways on from a free tool: the vectorizer, and the agent integrations. */
export function NextSteps({ agentText }: { agentText: ReactNode }) {
  return (
    <section className="px-6 py-12 sm:py-16 bg-white border-t border-cream-200">
      <div className="max-w-[960px] mx-auto grid gap-4 md:grid-cols-2">
        <div className="flex flex-col rounded-2xl border border-cream-200 bg-cream-50 p-6">
          <ImageIcon className="h-5 w-5 text-sky" aria-hidden="true" />
          <h2 className="mt-3 text-[20px] font-semibold tracking-[-0.01em] text-ink">Have a PNG or JPG? Vectorize it</h2>
          <p className="mt-2 text-[14px] leading-relaxed text-ink-muted">
            SVG.new traces PNG, JPG and WebP images into SVG paths you can edit, recolor and download. Your first
            conversion needs no account.
          </p>
          <div className="mt-auto pt-5">
            <Link href="/png-to-svg" className={btnPrimary}>
              Convert PNG to SVG
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </div>
        </div>
        <div className="flex flex-col rounded-2xl border border-cream-200 bg-cream-50 p-6">
          <Terminal className="h-5 w-5 text-sky" aria-hidden="true" />
          <h2 className="mt-3 text-[20px] font-semibold tracking-[-0.01em] text-ink">Do this from Claude Code or Codex</h2>
          <p className="mt-2 text-[14px] leading-relaxed text-ink-muted">{agentText}</p>
          <div className="mt-auto pt-5 flex flex-wrap gap-2">
            <Link href="/claude-code" className={btnSecondary}>
              Claude Code
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
            <Link href="/codex" className={btnSecondary}>
              Codex
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

const TOOLS = [
  { href: '/svg-viewer', label: 'SVG viewer and optimizer', blurb: 'See an SVG next to its code and shrink it with SVGO.' },
  { href: '/svg-to-react', label: 'SVG to React (JSX)', blurb: 'A typed React component with React prop names and unique ids.' },
  { href: '/svg-to-data-uri', label: 'SVG to data URI', blurb: 'URL-encoded and base64 data URIs, with CSS and <img> snippets.' },
  { href: '/png-to-svg', label: 'PNG to SVG', blurb: 'Trace a PNG, JPG or WebP into an SVG you can edit.' },
];

export function MoreTools({ current }: { current: string }) {
  return (
    <section className="px-6 py-12 sm:py-16 bg-cream-100/50 border-t border-cream-200">
      <div className="max-w-[960px] mx-auto">
        <h2 className="text-[20px] sm:text-[22px] font-semibold tracking-[-0.01em] text-ink">More free SVG tools</h2>
        <ul className="mt-5 grid gap-3 sm:grid-cols-3">
          {TOOLS.filter((t) => t.href !== current).map((t) => (
            <li key={t.href}>
              <Link
                href={t.href}
                className="block h-full rounded-xl border border-cream-200 bg-white p-4 hover:border-cream-300 transition-colors"
              >
                <span className="text-[15px] font-semibold text-ink">{t.label}</span>
                <span className="mt-1 block text-[13px] leading-relaxed text-ink-muted">{t.blurb}</span>
              </Link>
            </li>
          ))}
        </ul>
        <p className="mt-6 text-[14px] text-ink-muted">
          All three tools run in your browser. For turning images into SVG, see the{' '}
          <Link href="/image-to-svg" className={linkClass}>image to SVG converter</Link> and the{' '}
          <Link href="/developers" className={linkClass}>API docs</Link>.
        </p>
      </div>
    </section>
  );
}
